import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentService } from '../../services/loanService';
import { formatDate, humanizeStatus } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const DOC_TYPES = [
  ['id_front', 'ID Card (Front)'], ['id_back', 'ID Card (Back)'], ['selfie', 'Selfie'],
  ['proof_of_address', 'Proof of Address'], ['bank_statement', 'Bank Statement'],
  ['pay_slip', 'Pay Slip'], ['other', 'Other'],
];

const REQUEST_STATUS_META = {
  pending:   { label: 'Awaiting Upload', color: '#ff9900', badgeClass: 'badge-warning' },
  fulfilled: { label: 'Uploaded',        color: '#5B8EFF', badgeClass: 'badge-info' },
  waived:    { label: 'Waived',          color: '#94a3b8', badgeClass: '' },
};

const DOC_STATUS_META = {
  pending:           { label: 'Pending Review',    badgeClass: 'badge-warning' },
  approved:          { label: 'Approved',           badgeClass: 'badge-success' },
  rejected:          { label: 'Rejected',           badgeClass: 'badge-danger' },
  resubmit_required: { label: 'Resubmit Required', badgeClass: 'badge-danger' },
};

function FulfillRow({ req, onFulfilled }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);

  const fulfillMutation = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return documentService.fulfillRequest(req.id, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-doc-requests'] });
      setOpen(false);
      setErr('');
      if (onFulfilled) onFulfilled();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Upload failed.'),
  });

  const rMeta = REQUEST_STATUS_META[req.status] || { label: req.status, color: '#94a3b8' };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      {/* Header row */}
      <div style={{
        padding: '0.75rem 1rem',
        background: 'var(--surface-alt)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{req.doc_type_display}</span>
          <span style={{
            fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 99,
            background: `${rMeta.color}18`, color: rMeta.color, border: `1px solid ${rMeta.color}30`,
          }}>
            {rMeta.label}
          </span>
          {req.loan_application_number && (
            <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', fontFamily: 'monospace' }}>
              {req.loan_application_number}
            </span>
          )}
        </div>

        {req.status === 'pending' && (
          <button
            className="btn btn-primary"
            style={{ fontSize: '0.78rem', padding: '0.3rem 0.875rem' }}
            onClick={() => setOpen(v => !v)}
          >
            {open ? 'Cancel' : 'Upload'}
          </button>
        )}

        {req.status === 'fulfilled' && req.fulfilled_document_status && (() => {
          const dMeta = DOC_STATUS_META[req.fulfilled_document_status] || { label: req.fulfilled_document_status, badgeClass: '' };
          return (
            <span className={`badge ${dMeta.badgeClass}`} style={{ fontSize: '0.72rem' }}>
              {dMeta.label}
            </span>
          );
        })()}
      </div>

      {/* Instructions */}
      {req.notes && (
        <div style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: 'var(--text-2)', borderBottom: '1px solid var(--border)' }}>
          <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>Instructions: </span>{req.notes}
        </div>
      )}

      {/* Resubmit reason */}
      {req.fulfilled_document_rejection_reason && (
        <div style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: '#FF4757', borderBottom: '1px solid var(--border)', background: 'rgba(255,71,87,0.04)' }}>
          <span style={{ fontWeight: 600 }}>Reason: </span>{req.fulfilled_document_rejection_reason}
        </div>
      )}

      {/* Upload form (expands when pending + Upload clicked) */}
      {open && (
        <div style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {err && <div className="alert alert-error" style={{ marginBottom: '0.25rem' }}><span>⚠</span><span>{err}</span></div>}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ margin: 0, flex: 1 }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>File (PDF, JPG, PNG — max 10MB)</label>
              <input className="form-input" type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png" />
            </div>
            <button
              className="btn btn-primary"
              disabled={fulfillMutation.isPending}
              onClick={() => {
                const f = fileRef.current?.files?.[0];
                if (!f) { setErr('Please select a file.'); return; }
                fulfillMutation.mutate(f);
              }}
            >
              {fulfillMutation.isPending ? 'Uploading…' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Documents() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [docType, setDocType] = useState('id_front');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: docRequestsData, isLoading: reqLoading } = useQuery({
    queryKey: ['my-doc-requests'],
    queryFn: () => documentService.getMyDocumentRequests().then(r => r.data.data),
  });
  const docRequests = Array.isArray(docRequestsData) ? docRequestsData : (docRequestsData?.results || []);
  const activeRequests = docRequests.filter(r => r.status !== 'waived');

  const { data, isLoading } = useQuery({
    queryKey: ['my-documents'],
    queryFn: () => documentService.getMyDocuments().then(r => r.data.data),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, type }) => {
      const fd = new FormData();
      fd.append('file', file); fd.append('doc_type', type);
      return documentService.upload(fd);
    },
    onSuccess: () => { qc.invalidateQueries(['my-documents']); setSuccess('Document uploaded!'); fileRef.current.value = ''; },
    onError: (err) => setError(err.response?.data?.message || 'Upload failed.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => documentService.deleteDocument(id),
    onSuccess: () => qc.invalidateQueries(['my-documents']),
  });

  const docs = data?.results || [];

  return (
    <div>
      <div className="page-header"><h1>Documents</h1><p>Upload and manage your supporting documents</p></div>

      {/* ── Requested Documents ── */}
      {(reqLoading || activeRequests.length > 0) && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>Requested Documents</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '1rem' }}>
            Your loan reviewer has requested the following documents. Upload each to continue your application.
          </p>
          {reqLoading ? <LoadingSpinner /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {activeRequests.map(req => (
                <FulfillRow key={req.id} req={req} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Upload Document ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Upload Document</h2>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0, minWidth: '200px' }}>
            <label className="form-label">Document Type</label>
            <select className="form-input" value={docType} onChange={e => setDocType(e.target.value)}>
              {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0, flex: 1 }}>
            <label className="form-label">File (PDF, JPG, PNG — max 10MB)</label>
            <input className="form-input" type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png" />
          </div>
          <button className="btn btn-primary" disabled={uploadMutation.isPending}
            onClick={() => { const f = fileRef.current?.files?.[0]; if (f) uploadMutation.mutate({ file: f, type: docType }); }}>
            {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>

      {/* ── Uploaded Documents ── */}
      <div className="card">
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Uploaded Documents</h2>
        {isLoading ? <LoadingSpinner /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Type</th><th>Filename</th><th>Status</th><th>Uploaded</th><th></th></tr></thead>
              <tbody>
                {docs.map(doc => (
                  <tr key={doc.id}>
                    <td>{humanizeStatus(doc.doc_type)}</td>
                    <td>{doc.original_filename}</td>
                    <td><span className={`badge ${doc.status === 'approved' ? 'badge-success' : doc.status === 'rejected' ? 'badge-danger' : 'badge-warning'}`}>{humanizeStatus(doc.status)}</span></td>
                    <td>{formatDate(doc.created_at)}</td>
                    <td>{doc.status === 'pending' && <button className="btn btn-danger" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={() => deleteMutation.mutate(doc.id)}>Delete</button>}</td>
                  </tr>
                ))}
                {docs.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '2rem' }}>No documents uploaded yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
